/**
 * Types pour le module Comptabilité Analytique
 * Conforme au backend analytic controller
 * 
 * Ventilation multi-axes (centres de coût, projets, départements)
 * Analyse de rentabilité détaillée — SYSCOHADA
 */

// ====== ENUMS ======

/** Type de compte analytique */
export type AnalyticAccountType = 'cost_center' | 'project' | 'department' | 'other';

// ====== INTERFACES ======

/** Compte analytique (réponse API) */
export interface AnalyticAccount {
  id: string;
  code: string;
  name: string;
  description?: string;
  type: AnalyticAccountType;
  parentId?: string | null;
  isActive: boolean;
  companyId?: string;
  createdAt: string;
  updatedAt?: string;
}

/** Payload de création d'un compte analytique */
export interface CreateAnalyticAccountPayload {
  code: string;
  name: string;
  description?: string;
  type: AnalyticAccountType;
  parentId?: string;
  isActive?: boolean;
}

/** Payload de mise à jour d'un compte analytique */
export interface UpdateAnalyticAccountPayload {
  code?: string;
  name?: string;
  description?: string;
  type?: AnalyticAccountType;
  parentId?: string | null;
  isActive?: boolean;
}

/** Ligne analytique (ventilation d'une ligne d'écriture) */
export interface AnalyticLine {
  id: string;
  journalLineId: string;
  analyticAccountId: string;
  analyticAccountCode?: string;
  analyticAccountName?: string;
  debit: number;
  credit: number;
  percentage: number;
  description?: string;
  fiscalYearId?: string;
  createdAt?: string;
}

/** Payload de création d'une ligne analytique */
export interface CreateAnalyticLinePayload {
  journalLineId: string;
  analyticAccountId: string;
  debit: number;
  credit: number;
  percentage?: number;
  description?: string;
  fiscalYearId?: string;
}

/** Filtres du rapport analytique */
export interface AnalyticReportFilter {
  fiscalYearId: string;
  analyticAccountId?: string;
  startDate?: string;
  endDate?: string;
}

/** Données d'un compte dans le rapport analytique */
export interface AnalyticReportAccountData {
  id: string;
  code: string;
  name: string;
  type: AnalyticAccountType;
  totalDebit: number;
  totalCredit: number;
  balance: number;
  lineCount: number;
}

/** Rapport analytique complet */
export interface AnalyticReportData {
  fiscalYearId: string;
  accounts: AnalyticReportAccountData[];
  totals: {
    totalDebit: number;
    totalCredit: number;
    accountCount: number;
  };
}

// ====== LABELS & HELPERS ======

export const ANALYTIC_ACCOUNT_TYPE_LABELS: Record<AnalyticAccountType, string> = {
  cost_center: 'Centre de coût',
  project: 'Projet',
  department: 'Département',
  other: 'Autre',
};

export const ANALYTIC_ACCOUNT_TYPE_COLORS: Record<AnalyticAccountType, { bg: string; text: string; icon: string }> = {
  cost_center: { bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-400',    icon: 'text-blue-500' },
  project:     { bg: 'bg-green-100 dark:bg-green-900/30',   text: 'text-green-700 dark:text-green-400',  icon: 'text-green-500' },
  department:  { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: 'text-purple-500' },
  other:       { bg: 'bg-gray-100 dark:bg-gray-800',        text: 'text-gray-600 dark:text-gray-400',    icon: 'text-gray-500' },
};
